import { useState } from "react";
import {
  Grid,
  TextField,
  Button,
  Typography,
  CircularProgress,
} from "@mui/material";
// import Layout from "../utils/Layout";
import AlertBar from "../utils/AlertBar";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { API_URL } from "../../config/environment";
import { useTranslation } from "react-i18next";
import { useTitle } from "../utils/useTitle";
import { useAnalyticsEventTracker } from "../utils/useAnalyticsEventTracker";

const SignUp = () => {
  useTitle("Sign Up");
  const eventTracker = useAnalyticsEventTracker(true);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    passwordConfirmation: "",
    jobTitle: "",
    company: "",
    companySector: "",
  });
  const [errors, setErrors] = useState({
    firstName: false,
    lastName: false,
    email: false,
    password: false,
    passwordConfirmation: false,
    jobTitle: false,
    company: false,
    companySector: false,
  });
  const [errorMessage, setErrorMessage] = useState("");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    // Clear the error for the current field when the user starts typing
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: false,
    }));
  };

  const handleFormSubmit = async () => {
    setIsLoading(true);
    // Validate form fields
    const formErrors = {};
    Object?.keys(formData).forEach((key) => {
      if (!formData[key]) {
        formErrors[key] = true;
      }
    });

    if (Object?.keys(formErrors).length > 0) {
      // If there are errors, set the state to show errors
      setErrors(formErrors);
      setErrorMessage(t("auth.alertMsg.error1"));
      setIsLoading(false);
      return;
    }

    if (formData.password !== formData.passwordConfirmation) {
      setErrors({ error: true });
      setErrorMessage(t("auth.alertMsg.error2"));
      setIsLoading(false);
      return;
    }

    // Placeholder for the API request logic
    const url = `${API_URL}/auth/register`;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      console.log(data);

      if (data.status === "success") {
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          password: "",
          passwordConfirmation: "",
          jobTitle: "",
          company: "",
          companySector: "",
        });
        navigate("/account-confirm");
        eventTracker({
          hitType: "sign_up",
        });
      } else {
        setErrors({ error: true });
        setErrorMessage(data.message);
      }
    } catch (error) {
      console.error("Error:", error);
      setErrors({ error: true });
      setErrorMessage(t("auth.alertMsg.error3"));
    } finally {
      // // Reset form data and errors after successful submission or error
      // setFormData({
      //   firstName: "",
      //   lastName: "",
      //   email: "",
      //   password: "",
      //   passwordConfirmation: "",
      //   jobTitle: "",
      //   company: "",
      //   companySector: "",
      // });
      setIsLoading(false);
    }
  };

  return (
    <>
      <Grid container>
        {/* Left Side (8 columns) */}
        <Grid
          item
          xs={6}
          container
          justify="center"
          alignItems="center"
          style={{ overflow: "hidden" }}
        >
          <Grid
            container
            direction="column"
            spacing={3}
            style={{ maxWidth: "800px", padding: "20px", marginLeft: "70px" }}
          >
            <Grid item>
              <h1>{t("auth.header.signUp")}</h1>
              <Grid container spacing={4}>
                <Grid item xs={6}>
                  <TextField
                    label={t("auth.firstName")}
                    variant="outlined"
                    fullWidth
                    required
                    value={formData.firstName}
                    onChange={handleInputChange}
                    name="firstName"
                    error={errors.firstName}
                    helperText={
                      errors.firstName && t("auth.requiredMsg.firstName")
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label={t("auth.lastName")}
                    variant="outlined"
                    fullWidth
                    required
                    value={formData.lastName}
                    onChange={handleInputChange}
                    name="lastName"
                    error={errors.lastName}
                    helperText={
                      errors.lastName && t("auth.requiredMsg.lastName")
                    }
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <TextField
                label={t("auth.email")}
                type="email"
                variant="outlined"
                fullWidth
                required
                value={formData.email}
                onChange={handleInputChange}
                name="email"
                error={errors.email}
                helperText={errors.email && t("auth.requiredMsg.email")}
              />
            </Grid>
            <Grid item>
              <TextField
                label={t("auth.jobTitle")}
                variant="outlined"
                fullWidth
                required
                value={formData.jobTitle}
                onChange={handleInputChange}
                name="jobTitle"
                error={errors.jobTitle}
                helperText={errors.jobTitle && t("auth.requiredMsg.jobTitle")}
              />
            </Grid>
            <Grid item>
              <Grid container spacing={4}>
                <Grid item xs={6}>
                  <TextField
                    label={t("auth.company")}
                    variant="outlined"
                    fullWidth
                    required
                    value={formData.company}
                    onChange={handleInputChange}
                    name="company"
                    error={errors.company}
                    helperText={errors.company && t("auth.requiredMsg.company")}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    label={t("auth.companySector")}
                    variant="outlined"
                    fullWidth
                    required
                    value={formData.companySector}
                    onChange={handleInputChange}
                    name="companySector"
                    error={errors.companySector}
                    helperText={
                      errors.companySector &&
                      t("auth.requiredMsg.companySector")
                    }
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item>
              <Grid container spacing={4}>
                <Grid item xs={6}>
                  <TextField
                    label={t("auth.password")}
                    type="password"
                    variant="outlined"
                    fullWidth
                    required
                    value={formData.password}
                    onChange={handleInputChange}
                    name="password"
                    error={errors.password}
                    helperText={
                      errors.password && t("auth.requiredMsg.password")
                    }
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    label={t("auth.passwordConfirmation")}
                    type="password"
                    variant="outlined"
                    fullWidth
                    required
                    value={formData.passwordConfirmation}
                    onChange={handleInputChange}
                    name="passwordConfirmation"
                    error={errors.passwordConfirmation}
                    helperText={
                      errors.passwordConfirmation &&
                      t("auth.requiredMsg.passwordConfirmation")
                    }
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="success"
                fullWidth
                size="large"
                style={{ height: "55px" }}
                onClick={handleFormSubmit}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  t("auth.header.signUp")
                )}
              </Button>
            </Grid>

            <Grid item>
              <Typography variant="subtitle1" gutterBottom>
                {t("auth.msg.signIn")}{" "}
                <Link to="/signin">{t("auth.header.signIn")}</Link>{" "}
              </Typography>
            </Grid>
          </Grid>
        </Grid>

        {/* Right Side (6 columns) */}
        <Grid item xs={6} style={{ overflow: "hidden" }}>
          <div style={{ width: "100%", height: "100vh" }}>
            <img
              src="assets/images/banner1.jpg"
              alt="Signup"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </div>
        </Grid>
      </Grid>

      <AlertBar
        open={Object.values(errors).some((error) => error)}
        message={errorMessage}
        severity={"error"}
        onClose={() => setErrors({})}
      />
    </>
  );
};

export default SignUp;
